export type DataMode = 'mock' | 'api';
export type TenantFeatureMode = 'tenant' | 'support-readonly';
export type FreshnessState = 'current' | 'aging' | 'stale' | 'source_error' | 'missing' | 'unknown';
export type MappingState = 'suggested' | 'confirmed' | 'rejected' | 'issue';
export type UserAccessState = 'active' | 'inactive' | 'pending' | 'rejected';
export type PlatformRequestState = 'new' | 'in_review' | 'approved' | 'rejected';
export type ExactDecimal = string | number;
export type PriceState = 'available' | 'missing_not_returned' | 'not_set' | 'source_invalid';
export type QuantityState = 'reported' | 'confirmed_zero' | 'expected_free' | 'missing_not_returned' | 'not_expected' | 'source_error';
export type CatalogOffersLoadState = 'not_loaded' | 'loading' | 'loaded' | 'source_error';
export type BackendTenantRole = 'owner' | 'mapping_manager' | 'manager' | 'logist';

export const APPROVED_TENANT_ROLES = [
  'Владелец',
  'Ответственный за сопоставления',
  'Менеджер',
  'Логист',
] as const;
export type TenantRole = (typeof APPROVED_TENANT_ROLES)[number];

export const BACKEND_TENANT_ROLE_LABELS: Record<BackendTenantRole, TenantRole> = {
  owner: 'Владелец',
  mapping_manager: 'Ответственный за сопоставления',
  manager: 'Менеджер',
  logist: 'Логист',
};

export const COMMERCIAL_ACCESS_STATES = [
  'Внутренний тест',
  'Пробный период',
  'Активная подписка',
  'Бесплатный доступ вручную',
  'Доступ приостановлен',
  'Отключён',
] as const;
export type CommercialAccessState = (typeof COMMERCIAL_ACCESS_STATES)[number];

export const PLATFORM_INTEGRATION_CLASSES = [
  'DataSota standard',
  'API поставщика / адаптер DataSota',
  'Файловый / custom adapter',
] as const;
export type PlatformIntegrationClass = (typeof PLATFORM_INTEGRATION_CLASSES)[number];

export interface MoneyValue {
  amount: ExactDecimal | null;
  currency: string | null;
  state?: PriceState;
}

export interface QuantityValue {
  amount: ExactDecimal | null;
  unit: string;
  state?: QuantityState;
  expectedAt?: string | null;
}

export interface SupplierOffer {
  id: string;
  supplierId: string;
  supplierName: string;
  supplierSku: string;
  warehouse: string;
  lot?: string;
  purchase: MoneyValue;
  rrp: MoneyValue;
  free: QuantityValue;
  expected: QuantityValue;
  freshness: FreshnessState;
  freshnessLabel: string;
  freshnessDetail: string;
  observedAt: string | null;
  lastSuccessAt?: string;
  sourceAsOf?: string | null;
}

export interface CatalogDiagnostic {
  message: string;
  requestId?: string;
  status?: number;
}

export interface CanonicalCatalogItem {
  id: string;
  article: string | null;
  name: string;
  brand: string | null;
  collection: string | null;
  unit: string;
  texture?: 'sand' | 'lines' | 'floral' | null;
  duplicateWarning?: string;
  warnings?: string[];
  supplierCount: number;
  warehouseCount: number;
  offers: SupplierOffer[];
  offersState?: CatalogOffersLoadState;
  offersDiagnostic?: CatalogDiagnostic;
}

export interface CatalogSearchResult {
  query: string;
  items: CanonicalCatalogItem[];
  mockNotice: string;
}

export interface CatalogOffersResult {
  catalogGuid: string;
  supplierCount: number;
  warehouseCount: number;
  offers: SupplierOffer[];
  state: 'loaded' | 'source_error';
  diagnostic?: CatalogDiagnostic;
}

export interface TenantAuthContext {
  user: {
    id: string;
    displayName: string;
    email: string;
  };
  tenant: {
    id: string;
    slug: string;
    name: string;
  };
  tenantRole: BackendTenantRole;
  tenantRoleLabel: TenantRole;
  mfaVerified: boolean;
}

export interface LoginInput {
  tenantSlug: string;
  email: string;
  password: string;
}

export type LoginResult =
  | { state: 'authenticated'; context: TenantAuthContext }
  | { state: 'mfa_required'; expiresAt: string };

export interface SupplierWarehouse {
  id: string;
  name: string;
  state: FreshnessState;
  stateLabel: string;
  lastSuccessfulUpdate: string;
}

export interface SupplierImportRecord {
  id: string;
  startedAt: string;
  result: 'success' | 'error';
  objectCount: number | null;
  note: string;
}

/** Tenant-visible supplier relationship and operational state. */
export interface TenantSupplierSummary {
  id: string;
  name: string;
  integrationMethod: string;
  lastSuccessfulUpdate: string;
  health: FreshnessState;
  healthLabel: string;
  healthDetail: string;
  safeErrorState: string;
  capabilities: string[];
  productCount: number;
  warehouseCount: number;
  mappingCoveragePercent: number;
  mappingCoverageLabel: string;
  needsAttention: number;
  fallbackReference?: string;
  warehouses: SupplierWarehouse[];
  recentImports: SupplierImportRecord[];
}

/** Platform registry entry; intentionally separate from TenantSupplierSummary. */
export interface PlatformSupplierIntegration {
  id: string;
  supplier: string;
  integrationClass: PlatformIntegrationClass;
  sourceMethod: string;
  health: FreshnessState;
  healthLabel: string;
  healthDetail: string;
  lastSuccessfulIngest: string;
  capabilities: string[];
  adapterVersion?: string;
  feedVersion?: string;
  fallbackReference?: string;
}

export interface MappingRecord {
  id: string;
  supplier: string;
  supplierProduct: string;
  supplierArticle: string;
  proposedCanonical: string;
  canonicalArticle: string;
  state: MappingState;
  evidence: string[];
  issueState?: 'open' | 'in_review' | 'resolved' | 'dismissed';
}

export interface TenantUser {
  id: string;
  name: string;
  email: string;
  role: TenantRole;
  state: UserAccessState;
  stateLabel: string;
  mfa: string;
  updatedAt: string;
}

export interface AccessRequest {
  id: string;
  tenant: string;
  company: string;
  employeeName: string;
  employeeEmail: string;
  requestedRole: TenantRole;
  requester: string;
  requestedAt: string;
  state: PlatformRequestState;
  stateLabel: string;
}

export interface CommercialAccess {
  id: string;
  tenant: string;
  state: CommercialAccessState;
  effectiveFrom?: string;
  expiresAt?: string;
  lastChangedAt: string;
  administrativeAction: string;
}

export interface SupportGrant {
  id: string;
  tenant: string;
  operator: string;
  expiresAt: string;
  state: 'active' | 'expired' | 'revoked';
}
